import * as Y from 'yjs'
import { ref, type Ref } from 'vue'

/**
 * Adaptadores para exponer colecciones Yjs (Y.Array, Y.Map) como refs
 * reactivas de Vue, manteniendo la sincronización bidireccional.
 *
 * Los stores de la app trabajan contra estas refs como si fueran arrays
 * locales; los cambios se propagan al Y.Doc (y de ahí al CRDT/Supabase)
 * automáticamente.
 */

/**
 * Crea una vista reactiva de un Y.Array<T>.
 *
 * - Lee el estado inicial del array y lo vuelca en un ref.
 * - Escucha cambios del Y.Array (locales o remotos vía CRDT) y actualiza el ref.
 * - Expone `push`, `update`, `remove` que mutan el Y.Array transaccionalmente
 *   (lo que dispara el sync sin bucles).
 *
 * El idField identifica cada elemento para update/remove.
 */
export function useYArrayView<T extends { id: string }>(
  yarray: Y.Array<T>,
  idField: keyof T = 'id',
): {
  items: Ref<T[]>
  push: (item: T) => void
  update: (id: string, patch: Partial<T>) => void
  remove: (id: string) => void
  replaceAll: (items: T[]) => void
} {
  const items = ref<T[]>(yarray.toArray()) as Ref<T[]>

  // Reaccionar a cambios del CRDT (remotos o locales).
  yarray.observe(() => {
    items.value = yarray.toArray()
  })

  function indexOf(id: string): number {
    return yarray.toArray().findIndex((it) => it[idField] === id)
  }

  function push(item: T) {
    yarray.doc?.transact(() => {
      yarray.push([item])
    })
  }

  function update(id: string, patch: Partial<T>) {
    yarray.doc?.transact(() => {
      const i = indexOf(id)
      if (i === -1) return
      const current = yarray.get(i)
      yarray.delete(i, 1)
      yarray.insert(i, [{ ...current, ...patch }])
    })
  }

  function remove(id: string) {
    yarray.doc?.transact(() => {
      const i = indexOf(id)
      if (i !== -1) yarray.delete(i, 1)
    })
  }

  /** Reemplaza todo el contenido (útil para la reconciliación inicial). */
  function replaceAll(newItems: T[]) {
    yarray.doc?.transact(() => {
      yarray.delete(0, yarray.length)
      yarray.push(newItems)
    })
  }

  return { items, push, update, remove, replaceAll }
}

/**
 * Crea una vista reactiva de un Y.Map<string, T>.
 * Útil para el doc-índice de la biblioteca (bookId → metadatos).
 */
export function useYMapView<V>(
  ymap: Y.Map<V>,
): {
  entries: Ref<Record<string, V>>
  set: (key: string, value: V) => void
  delete: (key: string) => void
} {
  const entries = ref<Record<string, V>>(ymap.toJSON()) as Ref<Record<string, V>>

  ymap.observe(() => {
    entries.value = ymap.toJSON()
  })

  function set(key: string, value: V) {
    ymap.doc?.transact(() => {
      ymap.set(key, value)
    })
  }

  function del(key: string) {
    ymap.doc?.transact(() => {
      ymap.delete(key)
    })
  }

  return { entries, set, delete: del }
}
